import React from 'react';
import parse, { attributesToProps, domToReact } from 'html-react-parser';

import { ReactMarkdown } from './react-markdown';
import { cn } from '../utils/cn';
import { H1, H2, P } from './typography';

export enum ChangeTypeEnum {
  FIXED = 'fixed',
  ADDED = 'added',
  CHANGED = 'changed',
}

type ReleaseNoteChangeType = {
  type: ChangeTypeEnum;
  major?: boolean;
  description: string;
};

export type ReleaseNoteChangeProps = {
  change: ReleaseNoteChangeType;
  className?: string;
};

export const ReleaseNoteLabel = ({
  major,
  type,
  className = 'bg-slate-700',
}: {
  major?: boolean;
  className?: string;
  type: ChangeTypeEnum;
}) => (
  <div className="inline-flex w-[7em]">
    <div>
      <div
        className={cn(
          'uppercase font-bold text-sm p-1 px-2 rounded flex items-center gap-1',
          className,
        )}
      >
        {major && <span>&#9733;</span>}
        {type}
      </div>
    </div>
  </div>
);

export const ReleaseNoteChange = ({
  change,
  className = 'bg-slate-700',
}: ReleaseNoteChangeProps) => {
  const { major, type, description } = change;
  return (
    <div className="flex">
      <ReleaseNoteLabel
        major={Boolean(major)}
        type={type}
        className={className}
      />

      <div className="flex-1">
        <ReactMarkdown options={{ components: { p: P } }}>
          {description}
        </ReactMarkdown>
      </div>
    </div>
  );
};

const extractType = (className = ''): ChangeTypeEnum | undefined => {
  let type;

  switch (className) {
    case ChangeTypeEnum.ADDED:
      type = ChangeTypeEnum.ADDED;
      break;
    case ChangeTypeEnum.CHANGED:
      type = ChangeTypeEnum.CHANGED;
      break;
    case ChangeTypeEnum.FIXED:
      type = ChangeTypeEnum.FIXED;
      break;
  }

  return type;
};

const extractVersion = (content = ''): string => {
  const version = content.split(' - ')[0];
  return version;
};

const hasStar = (content = ''): boolean =>
  content.includes('★') || content.includes('&#9733;');

const StarsText = () => (
  <P className="opacity-75">&#9733; Stars denote major new features.</P>
);

export const ReleaseNotesHtml = ({
  html,
  colorMap,
  title,
}: {
  html: string;
  colorMap?: Record<ChangeTypeEnum, string>;
  title: string;
}) => {
  const includesMajorChanges = hasStar(html);

  const items = html
    .split('\n')
    .filter((item) => item.trim() !== '')
    .map((item) => {
      const parts = item.split('</span>');
      const type = `${parts[0]}</span>`;
      const content = parts.slice(1).join('</span>');
      return { type, content };
    });

  return (
    <div className="flex flex-col gap-5">
      <H1>{title}</H1>
      {includesMajorChanges && <StarsText />}
      {items.map((item, i) => {
        return (
          <div key={i} className="flex">
            <div>
              {parse(item.type, {
                replace: (node: any) => {
                  if (node?.name === 'h2') {
                    const version = extractVersion(node?.children?.[0]?.data);
                    return <H2 className="!m-0 !text-3xl">{version}</H2>;
                  }
                  if (node?.name === 'span') {
                    const className = node?.attribs?.class || '';
                    const type = extractType(className);

                    if (type) {
                      return (
                        <ReleaseNoteLabel
                          type={type}
                          className={colorMap?.[type]}
                          major={hasStar(item.type)}
                        />
                      );
                    }
                  }
                },
              })}
            </div>

            <div>
              {parse(item.content, {
                replace: (node: any) => {
                  if (node?.name === 'code') {
                    const props = attributesToProps(node.attribs);
                    return (
                      <code
                        {...props}
                        className="px-[0.4em] py-[0.2em] bg-foreground/10 rounded text-xs"
                      >
                        {domToReact(node.children)}
                      </code>
                    );
                  }

                  if (node?.attribs?.class === 'button') {
                    return (
                      <span className="px-[2px] font-bold rounded-sm border">
                        {domToReact(node.children)}
                      </span>
                    );
                  }

                  return node;
                },
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const ReleaseNoteChanges = ({
  changes,
  title,
  colorMap,
}: {
  title?: string;
  colorMap?: Record<ChangeTypeEnum, string>;
  changes?: ReleaseNoteChangeType[];
  html?: string;
}) => {
  return (
    <div className="flex flex-col gap-5">
      {title && <H2>{title}</H2>}
      {changes?.map((change, index) => (
        <ReleaseNoteChange
          key={index}
          change={change}
          className={colorMap?.[change.type]}
        />
      ))}
    </div>
  );
};

export type ReleaseNoteListProps = {
  releaseNotes: {
    categoryName?: string;
    changes: ReleaseNoteChangeType[];
  }[];
  colorMap?: Record<ChangeTypeEnum, string>;
  title: string;
};

export const ReleaseNoteList = ({
  releaseNotes,
  colorMap,
  title,
}: ReleaseNoteListProps) => {
  const includesMajorChanges = releaseNotes
    .flatMap((noteCategory) => noteCategory.changes || [])
    .some((note) => note.major);

  return (
    <div className="flex flex-col gap-5">
      <H1>{title}</H1>

      {includesMajorChanges && <StarsText />}

      {releaseNotes.map((noteCategory, index) => (
        <ReleaseNoteChanges
          key={index}
          colorMap={colorMap}
          changes={noteCategory.changes || []}
          title={noteCategory.categoryName}
        />
      ))}
    </div>
  );
};
